import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { addDays } from 'date-fns';

import LegendLeftContainer from '@/apps/mwd-v2/components/Legends/Left/LegendLeftContainer';
import LegendRightContainer from '@/apps/mwd-v2/components/Legends/Right/LegendRightContainer';
import { MaskedElement } from '@/apps/mwd-v2/components/MaskedElement/MaskedElement';
import TwoAMTimeLine from '@/apps/mwd-v2/components/Timescale/TwoAMTimeLine';
import { clearScroll } from '@/apps/mwd-v2/state/actions/scrollToTime';
import useScrollStore from '@/apps/mwd-v2/state/selectors/useScrollStore';
import ChevronUp from '@/assets/icons/ChevronUp';
import TrainAnimation from '@/components/easterImage/TrainAnimation';
import useClientState from '@/state/useClientState';

import { legendLeftTotalWidth, legendRightTotalWidth, mwdScrollAnchorOffsetPx } from '../../utils/timescale';
import ContextMenu from '../ContextMenu/ContextMenu';
import CurrentTimeLine from '../Timescale/CurrentTimeLine';
import TickDays from '../Timescale/TickDays';
import TickHours from '../Timescale/TickHours';
import TickSvg from '../Timescale/TickSvg';
import Timeline from '../Timescale/Timeline';

import MwdSvg from './MwdSvg';
import useMwdChart from './useMwdChart';

import styles from './MwdChart.module.css';
type MwdChartProps = {
  children: ReactNode | ReactNode[];
};

const MwdChart: FC<MwdChartProps> = ({ children }) => {
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  const { scrollEventHandler, mwdHeight, mwdWidth } = useMwdChart();
  const { scrollX, scrollY, scrollBehavior, scrollLeft, scale } = useScrollStore();
  const end = useClientState((state) => state.mwd.end);

  const mwdChartRef = useRef<HTMLDivElement>(null);

  const scrollToPositionX = (x: number, y?: number, behavior: 'instant' | 'smooth' = 'smooth') => {
    const scrollOptions: ScrollToOptions = {
      left: x - mwdScrollAnchorOffsetPx,
      behavior: behavior as ScrollBehavior,
    };
    if (y !== undefined) {
      scrollOptions.top = y - mwdScrollAnchorOffsetPx;
    }
    mwdChartRef.current?.scrollTo(scrollOptions);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!mwdChartRef.current) return;
      const { scrollTop } = mwdChartRef.current;
      setIsScrolledToBottom(scrollTop > 100);
    };

    const scrollContainer = mwdChartRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      scrollContainer?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    // Scroll to the current time on scrollX change (right legend click).
    if (scrollX !== undefined) {
      scrollToPositionX(scrollX, scrollY, scrollBehavior ?? 'smooth');
      clearScroll();
    }
  }, [scrollX, scrollY, scrollBehavior, scrollLeft, scale]);

  useEffect(() => {
    // scroll to now by default.
    if (mwdChartRef?.current) scrollToPositionX(scale(new Date()), undefined, 'instant');
  }, []);

  const dayAfterTomorrow = useMemo(() => scale(addDays(end, -1)), [scale, end]);

  return (
    <div className={styles.mwdChartContainer}>
      {isScrolledToBottom && (
        <div className={styles.mwdChartScrolledToBottomContainer}>
          <ChevronUp
            height='32px'
            width='32px'
            onClick={() => mwdChartRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
            className={styles.mwdChartScrolledToBottomIcon}
          />
        </div>
      )}
      <MaskedElement variant='bottomRight' />

      <MaskedElement variant='bottomLeft' />
      <div ref={mwdChartRef} id='mwdScrollContainer' className={styles.mwdChart} onScrollCapture={scrollEventHandler}>
        <LegendLeftContainer />
        <div
          data-testid='mwdSvgContainer'
          id='mwdSvgContainer'
          style={{
            minHeight: `${mwdHeight}px`,
            height: 'auto',
            width: 'auto',
            background: `linear-gradient(90deg, var(--tractie-color-background-default) ${dayAfterTomorrow}px, var(--tractie-color-background-accent-gray-subtle) ${dayAfterTomorrow}px)`,
            marginLeft: `${legendLeftTotalWidth}`,
            marginRight: `${legendRightTotalWidth}`,
          }}
        >
          <TickSvg mwdWidth={mwdWidth}>
            <TickDays top={20} />
            <TickHours top={30} />
          </TickSvg>

          <MwdSvg
            data-timescale-start={scale.domain()[0]}
            data-timescale-end={scale.domain()[1]}
            data-timescale-range={scale.range()}
            mwdHeight={mwdHeight}
            mwdWidth={mwdWidth}
          >
            <Timeline />
            <CurrentTimeLine />

            <TwoAMTimeLine />

            {children}
          </MwdSvg>

          <ContextMenu />

          <TickSvg bottom mwdWidth={mwdWidth}>
            <TickHours orientation='bottom' top={0} />
          </TickSvg>
        </div>
        <TrainAnimation />
        <LegendRightContainer />
      </div>
    </div>
  );
};
export default MwdChart;
